import { parseEmsDate } from "../../Logic/emsDates";
import { ProcessingOutcome, ProcessingTimelineEntry } from "../EmailDetailAPI";
import { absent } from "./emailFields";

interface iTimelineProps {
    /** Oldest to newest, as the server ordered them. Never re-sorted here. */
    entries: ProcessingTimelineEntry[];
}

/**
 * The rail dot's class and the outcome-text's class, per outcome, decided
 * together in one `Record` rather than two.
 *
 * A chain of `if`s here would have rendered a new outcome unstyled with
 * every test still green — the reason `OUTCOME_DOT` used to be a `Record` in
 * the first place. But a lone `Failed`/`DeadLettered` check on the *text*
 * span was exactly that chain in miniature: it silently let a sixth outcome
 * read as a fault on the dot and as neutral on the label. Folding both
 * treatments into one entry per outcome makes that split impossible to
 * reintroduce by accident — there is nowhere to update only one of them.
 * Shape as well as hue on the dot — filled, hollow, ringed — though that
 * separates the groups rather than all five: `Succeeded`, `Failed` and
 * `Reprocessed` are each a solid disc at near-identical luminance and differ
 * only in hue. Nothing is lost to that, because the outcome's own word
 * renders beside the dot on every row — the dot is a fast second signal, not
 * what carries the meaning. `STATUS_PILL` in emailFields separates its five
 * more cleanly, but a pill has room for a border a 9px dot does not.
 */
const OUTCOME_TREATMENT: Record<ProcessingOutcome, { dot: string; text: string }> = {
    Succeeded: { dot: "mp-tl-dot mp-tl-dot-ok", text: "mp-tl-outcome" },
    Started: { dot: "mp-tl-dot mp-tl-dot-start", text: "mp-tl-outcome" },
    Failed: { dot: "mp-tl-dot mp-tl-dot-fail", text: "mp-tl-outcome mp-tl-outcome-fail" },
    DeadLettered: { dot: "mp-tl-dot mp-tl-dot-dead", text: "mp-tl-outcome mp-tl-outcome-fail" },
    Reprocessed: { dot: "mp-tl-dot mp-tl-dot-reprocessed", text: "mp-tl-outcome" },
};

/**
 * Composed onto the rail dot — never in place of it — when a human
 * (support) triggered the entry, so a support-triggered `Failed` still
 * shows the fault dot, with this marker sitting alongside it rather than
 * hiding it.
 *
 * Its own named constant on purpose: reaching into
 * `OUTCOME_TREATMENT.Reprocessed` by name to mean "a human did this" would
 * weld two independent decisions together by identity. That entry styles a
 * *system*-initiated reprocess; this one styles *any* support-triggered
 * entry, reprocess or not. Keeping them apart means a future change to how
 * an automatic reprocess looks cannot silently restyle every support action
 * too.
 */
const HUMAN_DOT_MODIFIER = "mp-tl-dot-human";

/**
 * A stamp, or the absent marker. Row timestamps are offset-less (contract
 * §1.3).
 *
 * The absent branch is the shared `absent()` — one dialect for "not measured"
 * across the feature — but the formatting stays local: `timeStyle: "medium"`,
 * not `emailFields.tsx`'s `clock()` `"short"`. A reprocess can journal two
 * entries within the same second (see the `key` comment below), so seconds are
 * the difference between two rows here, not noise to trim as they are in the
 * mailbox table. Sharing the marker is not a reason to share the clock.
 */
function when(stamp: string) {
    const parsed = parseEmsDate(stamp);
    if (!parsed) {
        return absent("Not recorded");
    }
    return parsed.toLocaleString([], { dateStyle: "short", timeStyle: "medium" });
}

function Timeline({ entries }: iTimelineProps) {
    if (entries.length === 0) {
        return (
            <p className="mp-tl-empty">
                No processing entries were returned for this email. The timeline is written as the pipeline runs, so an
                email that has not been picked up yet legitimately has none.
            </p>
        );
    }

    return (
        <ol className="mp-tl">
            {entries.map((item, index) => {
                // Keyed on the index as well as the stamp. A reprocess can
                // journal two entries within the same second, and the optimistic
                // apply appends one more — so the stamp alone is not unique, and
                // React calls a duplicate key unsupported rather than dropping it.
                const key = `${index}-${item.occurredAt}`;
                // Spec §3.3: the user id is the marker, never `reason`.
                const bySupport = item.triggeredByUserID !== undefined;
                // A whitespace-only note is, for display, no note: `SendEmailAction`
                // trims a blank note to `undefined` before it is ever sent, so a
                // note of "  " reaching this view means the same "no note" the
                // absent case means — not a quoted blank, which would read as a
                // rendering fault.
                const note = item.reason?.trim();
                return (
                    <li key={key}>
                        <div className="mp-tl-rail">
                            <span
                                className={
                                    bySupport
                                        ? `${OUTCOME_TREATMENT[item.outcome].dot} ${HUMAN_DOT_MODIFIER}`
                                        : OUTCOME_TREATMENT[item.outcome].dot
                                }
                            />
                        </div>
                        <div className="mp-tl-main">
                            <span
                                className="mp-tl-stage"
                                data-testid="timeline-stage"
                            >
                                {item.stage}
                            </span>
                            <span className={OUTCOME_TREATMENT[item.outcome].text}>{item.outcome}</span>
                            {/*
                                `!== undefined`, never a truthiness check: attempt
                                0 is a real report — the entry exists and no retry
                                has happened — and `{item.attempt && …}` would
                                render nothing for it, which reads as "not a send
                                attempt". Same split `attempts` draws in EmailTable.
                            */}
                            {item.attempt !== undefined && <span className="mp-tl-chip">attempt {item.attempt}</span>}
                            {bySupport && (
                                <span className="mp-tl-chip mp-tl-chip-human">
                                    Support · user {item.triggeredByUserID}
                                </span>
                            )}
                        </div>
                        <div className="mp-tl-when">{when(item.occurredAt)}</div>
                        {/*
                            Duplicates all three inner guards rather than just
                            letting them each render nothing: without this, an
                            entry with none of the three still mounts an empty
                            `.mp-tl-detail`, and Task 11's margin on that class
                            would draw on every ordinary row, not just the ones
                            with something to show.
                        */}
                        {(item.failureReason !== undefined || item.failureStackTrace !== undefined || bySupport) && (
                            <div className="mp-tl-detail">
                                {item.failureReason !== undefined && (
                                    <div className="mp-tl-fail">{item.failureReason}</div>
                                )}
                                {bySupport && (
                                    <div className="mp-tl-note">
                                        {note ? (
                                            `“${note}”`
                                        ) : (
                                            // The note is optional on both POSTs, so
                                            // this is a real and common state. Said
                                            // plainly, because a blank line here would
                                            // read as a rendering fault. A whitespace-only
                                            // note collapses to this too — see `note` above.
                                            <span className="mp-tl-note-none">No reason given</span>
                                        )}
                                    </div>
                                )}
                                {item.failureStackTrace !== undefined && (
                                    /*
                                        Collapsed, and capped in styles.css.
                                        `failureStackTrace` is unbounded — a
                                        400-line trace left open would push the
                                        rest of the timeline off the page, and
                                        the timeline is what the operator opened
                                        this view to read.
                                    */
                                    <details className="mp-tl-trace">
                                        <summary>Stack trace</summary>
                                        <pre className="mp-tl-trace-body">{item.failureStackTrace}</pre>
                                    </details>
                                )}
                            </div>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}

export default Timeline;
